import { PageProps } from 'libs/contentful/queries/page/props';
import { CustomQueryStringParameterItems } from 'libs/contentful/props';

/**
 * Returns the custom query string parameter items for use in client components.
 * Uses server-resolved items when present (variant-aware); otherwise falls back to
 * section baseline or inline collection. Safe to call from 'use client' components—
 * does not import or use server-only code (e.g. getExperiences).
 */
export function getCustomQueryStringParametersItemsForClient(
  page: PageProps | undefined,
): CustomQueryStringParameterItems[] {
  if (!page) return [];
  if (page.resolvedCustomQueryStringParametersItems?.length) {
    return page.resolvedCustomQueryStringParametersItems;
  }
  const sectionItems = page.customQueryStringParameters?.customQueryStringParametersCollection?.items ?? [];
  const inlineItems = page.customQueryStringParametersCollection?.items ?? [];
  return sectionItems.length ? sectionItems : inlineItems;
}

/**
 * Extracts custom QSP items from a Ninetailed variant payload (e.g. state.variant from onSelectVariant).
 * Supports both collection and inline shapes. Returns null if variant has no items.
 */
export function getCustomQspsItemsFromVariant(variant: unknown): CustomQueryStringParameterItems[] | null {
  if (!variant || typeof variant !== 'object') return null;
  const v = variant as {
    customQueryStringParametersCollection?: { items: CustomQueryStringParameterItems[] };
    customQueryStringParameters?: { items: CustomQueryStringParameterItems[] };
  };
  const items =
    v.customQueryStringParametersCollection?.items ??
    (v.customQueryStringParameters && Array.isArray(v.customQueryStringParameters.items)
      ? v.customQueryStringParameters.items
      : undefined);
  return items?.length ? items : null;
}

/**
 * Resolves custom QSP items for a given variant index map (e.g. from Ninetailed preview plugin).
 * Used when previewing variants client-side so links and enrollment URLs use the selected variant's params.
 * Does not use server-only getExperiences().
 */
export function getCustomQspsItemsForVariantIndexMap(
  page: PageProps,
  variantIndexByExperienceId: Record<string, number>,
): CustomQueryStringParameterItems[] {
  const section = page.customQueryStringParameters;
  const sectionItems = section?.customQueryStringParametersCollection?.items ?? [];
  const inlineItems = page.customQueryStringParametersCollection?.items ?? [];
  const fallback = sectionItems.length > 0 ? sectionItems : inlineItems;

  const variantItems = tryGetVariantItemsFromMap(page, section, variantIndexByExperienceId);
  return variantItems?.length ? variantItems : fallback;
}

function tryGetVariantItemsFromMap(
  page: PageProps,
  section: PageProps['customQueryStringParameters'],
  variantIndexByExperienceId: Record<string, number>,
): CustomQueryStringParameterItems[] | null {
  if (!section?.sys?.id || !page.ninetailedData) return null;

  const ninetailedData = page.ninetailedData.get(section.sys.id);
  if (!ninetailedData?.mappedExperiences?.length) return null;

  const sectionId = section.sys.id;
  const hasSection = (exp: (typeof ninetailedData.mappedExperiences)[number]) =>
    exp.components?.some(comp => 'id' in comp.baseline && comp.baseline.id === sectionId);

  // Prefer the experience that is selected in the preview (present in variantIndexByExperienceId)
  const selectedExperience =
    Object.keys(variantIndexByExperienceId).length > 0
      ? ninetailedData.mappedExperiences.find(
          exp => hasSection(exp) && variantIndexByExperienceId[exp.id] !== undefined,
        )
      : undefined;

  const experience = selectedExperience ?? ninetailedData.mappedExperiences.find(hasSection);
  if (!experience?.components) return null;

  const component = experience.components.find(comp => 'id' in comp.baseline && comp.baseline.id === sectionId);
  if (!component) return null;

  const variantIndex =
    variantIndexByExperienceId[experience.id] !== undefined ? variantIndexByExperienceId[experience.id] : 0;
  const selectableVariants = [component.baseline, ...(component.variants ?? [])];
  const variant = selectableVariants[variantIndex];
  if (!variant || typeof variant !== 'object') return null;

  const v = variant as {
    customQueryStringParametersCollection?: { items: CustomQueryStringParameterItems[] };
    customQueryStringParameters?: { items: CustomQueryStringParameterItems[] };
  };
  const items =
    v.customQueryStringParametersCollection?.items ??
    (v.customQueryStringParameters && Array.isArray(v.customQueryStringParameters.items)
      ? v.customQueryStringParameters.items
      : undefined);

  return items?.length ? items : null;
}
